/**
 * Validates HTML string to ensure it's safe to render
 * Checks for common HTML errors including unclosed tags, mismatched tags, and invalid nesting
 * @param {string} html - The HTML string to validate
 * @returns {Object} - { isValid: boolean, errors: string[], sanitizedHtml: string }
 */
export const validateHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return {
      isValid: false,
      errors: ['HTML is empty or not a string'],
      sanitizedHtml: '',
    };
  }

  const errors = [];
  const tagStack = [];

  // Self-closing tags that don't need a closing tag
  const selfClosingTags = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);

  // Void elements that should not have closing tags
  const voidElements = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);

  try {
    // Remove comments to avoid false positives
    const processedHtml = html.replace(/<!--[\s\S]*?-->/g, '');

    // Find all opening and closing tags
    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
    let match = tagRegex.exec(processedHtml);

    while (match !== null) {
      const fullTag = match[0];
      const tagName = match[1].toLowerCase();
      const isClosingTag = fullTag.startsWith('</');
      const isSelfClosing = fullTag.endsWith('/>') || selfClosingTags.has(tagName);

      if (isClosingTag) {
        // Check if it's a void element (shouldn't have closing tag)
        if (voidElements.has(tagName)) {
          errors.push(`Void element <${tagName}> should not have a closing tag`);
        } else if (tagStack.length === 0) {
          // Check if there's a matching opening tag
          errors.push(`Closing tag </${tagName}> found without matching opening tag`);
        } else {
          const lastOpenTag = tagStack.pop();
          if (lastOpenTag !== tagName) {
            errors.push(`Mismatched tags: expected </${lastOpenTag}> but found </${tagName}>`);
            // Try to recover by pushing back the tag
            tagStack.push(lastOpenTag);
          }
        }
      } else if (!isSelfClosing && !voidElements.has(tagName)) {
        // Opening tag that needs a closing tag
        tagStack.push(tagName);
      }

      match = tagRegex.exec(processedHtml);
    }

    // Check for unclosed tags
    if (tagStack.length > 0) {
      tagStack.forEach((tag) => {
        errors.push(`Unclosed tag: <${tag}>`);
      });
    }

    // Check for common malformed patterns
    if (/<[^>]*$/.test(processedHtml)) {
      errors.push('Incomplete tag found at the end of HTML');
    }

    // Check for unescaped angle brackets in content
    const contentBetweenTags = processedHtml.replace(/<[^>]+>/g, '');
    if (/</.test(contentBetweenTags) && !/<\s*br\s*\/?>/.test(contentBetweenTags)) {
      errors.push('Potentially unescaped < character found in content');
    }

    const isValid = errors.length === 0;

    return {
      isValid,
      errors,
      sanitizedHtml: isValid ? html : '',
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Validation error: ${error.message}`],
      sanitizedHtml: '',
    };
  }
};

/**
 * Sanitizes HTML by attempting to fix common issues
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML or empty string if critically invalid
 */
export const sanitizeHtml = (html) => {
  const validation = validateHtml(html);

  if (validation.isValid) {
    return html;
  }

  // eslint-disable-next-line no-console
  console.warn('HTML validation errors:', validation.errors);

  // Return empty string for safety if HTML is invalid
  // In production, you might want to try auto-fixing or use a library like DOMPurify
  return '';
};
